import * as React from "react";

import { RowProps, useController } from "./row.controller";
import { Flex } from "@common/components/Flex";
import { TextField } from "./components/text-field";
import { FieldType } from "@common/dto/field/type";
import { isNumber, stringHasInteger } from "@common/utils/string";
import Dropdown, {
  components,
  DROPDOWN_NO_VALUE,
} from "@common/containers/fields/Dropdown";
import { Checkbox } from "@common/components/checkbox";
import cx from "classnames";
import DatePicker from "react-datepicker";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { parseApiDateString } from "@modules/datasheets/utils/date-validation";

import "./row.styles.scss";

function blurActiveElementSoon(): void {
  window.setTimeout(() => {
    const el = document.activeElement;
    if (el instanceof HTMLElement) {
      el.blur();
    }
  }, 0);
}

const ColoredOption = (props: any) => {
  const { data } = props;
  const optionColor = data?.color;
  const fallbackColor = props?.selectProps?.dropdownContentColor;
  const resolvedColor = optionColor ?? fallbackColor;

  return (
    <components.Option {...props}>
      <span style={{ color: resolvedColor || undefined }}>
        {props.children}
      </span>
    </components.Option>
  );
};

const ColoredSingleValue = (props: any) => {
  const { data } = props;
  const optionColor = data?.color;
  const fallbackColor = props?.selectProps?.dropdownContentColor;
  const resolvedColor = optionColor ?? fallbackColor;

  return (
    <components.SingleValue {...props}>
      <span style={{ color: resolvedColor || undefined }}>
        {props.children}
      </span>
    </components.SingleValue>
  );
};

export const Row: React.FC<RowProps> = (props) => {
  const { columns, selected, data, onCellChange, editable, tableContentStyle } =
    props;

  const { handleClick } = useController(props);

  // Track raw typed value per date field — react-datepicker may clear/reformat
  // the input on blur, so we capture keystrokes via onChangeRaw.
  const dateRawInputRef = React.useRef<Record<number, string>>({});
  const dropdownJustCommittedFieldIdsRef = React.useRef<Set<number>>(new Set());

  /**
   * Compute text/font styles for a table subfield so that
   * bold/italic/underline and font settings configured for the field
   * are visible in the result table.
   */
  const getFieldContentStyle = (field: any): React.CSSProperties => {
    if (!field) {
      return {};
    }

    // Decode font color from numeric BGR value (same logic as useFieldContentStyle)
    const fontcolorNum =
      typeof field.fontcolor === "number" ? field.fontcolor : 0;
    const blue = fontcolorNum & 0xff;
    const green = (fontcolorNum >> 8) & 0xff;
    const red = (fontcolorNum >> 16) & 0xff;
    const hex = [
      red.toString(16).padStart(2, "0"),
      green.toString(16).padStart(2, "0"),
      blue.toString(16).padStart(2, "0"),
    ].join("");

    const contentColor = `#${hex}`;

    const alignRaw =
      (field as any).contentalign ?? DatasheetFieldComponentTextAlign.left;
    let textAlign: React.CSSProperties["textAlign"] = "left";
    switch (alignRaw) {
      case DatasheetFieldComponentTextAlign.center:
        textAlign = "center";
        break;
      case DatasheetFieldComponentTextAlign.right:
        textAlign = "right";
        break;
      default:
        textAlign = "left";
    }

    const contentFontFamily =
      (field as any).fontname ||
      (field as any).fontName ||
      (field as any).styleFontName ||
      "Arial";
    const contentFontSize =
      (field as any).fontsize ||
      (field as any).fontSize ||
      (field as any).styleFontSize ||
      10;

    const textStyleValue = (field as any).textstyle ?? (field as any).textStyle;
    let contentTextStyles: Array<DatasheetComponentTextStyle> = [];
    if (Array.isArray(textStyleValue)) {
      contentTextStyles = textStyleValue.filter(
        (s): s is DatasheetComponentTextStyle =>
          s === DatasheetComponentTextStyle.bold ||
          s === DatasheetComponentTextStyle.italic ||
          s === DatasheetComponentTextStyle.underline,
      );
    } else if (
      typeof textStyleValue === "number" &&
      textStyleValue !== 0 &&
      textStyleValue !== 33
    ) {
      const styles: Array<DatasheetComponentTextStyle> = [];
      if (
        (textStyleValue & DatasheetComponentTextStyle.bold) ===
        DatasheetComponentTextStyle.bold
      ) {
        styles.push(DatasheetComponentTextStyle.bold);
      }
      if (
        (textStyleValue & DatasheetComponentTextStyle.italic) ===
        DatasheetComponentTextStyle.italic
      ) {
        styles.push(DatasheetComponentTextStyle.italic);
      }
      if (
        (textStyleValue & DatasheetComponentTextStyle.underline) ===
        DatasheetComponentTextStyle.underline
      ) {
        styles.push(DatasheetComponentTextStyle.underline);
      }
      contentTextStyles = styles;
    }

    return {
      fontFamily: contentFontFamily,
      fontSize: contentFontSize,
      color: contentColor,
      textAlign,
      fontWeight: contentTextStyles.includes(DatasheetComponentTextStyle.bold)
        ? "bold"
        : undefined,
      fontStyle: contentTextStyles.includes(DatasheetComponentTextStyle.italic)
        ? "italic"
        : undefined,
      textDecoration: contentTextStyles.includes(
        DatasheetComponentTextStyle.underline,
      )
        ? "underline"
        : undefined,
    };
  };

  const getCellStringValue = (fieldId: number): string => {
    const cell = data.cells?.[fieldId];
    const raw = cell?.rawValue;
    const display = cell?.displayValue;
    const dataValue = cell?.dataValue;
    const v = raw ?? dataValue ?? display ?? "";
    if (v === null || v === undefined) {
      return "";
    }
    return String(v);
  };

  const normalizeDropdownOptions = (
    field: any,
  ): Array<{ label: string; value: any; color?: string }> => {
    const dropdownValues = field?.dropdownValues;
    if (Array.isArray(dropdownValues) && dropdownValues.length > 0) {
      return dropdownValues.map((v: any) => ({ value: v, label: String(v) }));
    }
    const options = field?.options;
    if (Array.isArray(options) && options.length > 0) {
      return options.map((o: any) => ({
        value: o.actualValue ?? o.value,
        label: o.displayValue ?? o.label ?? o.actualValue ?? o.value,
        color: o.color ?? o.optionColor ?? undefined,
      }));
    }
    return [];
  };

  // Helper to check if click target is inside a dropdown menu (not the control)
  const isDropdownMenuElement = React.useCallback(
    (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return false;

      // Check for react-select dropdown menu (portal rendered in body)
      // Only check for the menu, not the control itself - we want to allow row selection when clicking the control
      if (
        target.closest('[class*="react-select__menu"]') ||
        target.closest('[class*="react-select__option"]') ||
        target.closest('[class*="react-select__menu-list"]') ||
        // Check for Dropdown menu classes
        target.closest(".Dropdown__menu") ||
        target.closest('[class*="Dropdown__menu"]') ||
        // Check if it's the menu portal (rendered outside the row)
        (target.closest('[class*="menu"]') &&
          target.closest('[class*="react-select"]') &&
          !target.closest(".react-select__control"))
      ) {
        return true;
      }

      // Check for native select dropdown (when open)
      if (
        target.closest("select") &&
        (target as HTMLElement).tagName !== "SELECT"
      ) {
        // If clicking inside a select's dropdown options
        return true;
      }

      // Check for react-datepicker dropdown menu
      if (
        target.closest(".react-datepicker__portal") ||
        (target.closest(".react-datepicker") &&
          !target.closest(".react-datepicker__input-container"))
      ) {
        return true;
      }

      return false;
    },
    [],
  );

  const buildDropdownStyles = (style?: React.CSSProperties) => {
    if (!style) {
      return undefined;
    }
    const {
      fontFamily,
      fontSize,
      color,
      fontWeight,
      fontStyle,
      textDecoration,
      textAlign,
    } = style;

    return {
      control: (base: any) => ({
        ...base,
        fontFamily,
        fontSize,
      }),
      valueContainer: (base: any) => ({
        ...base,
        textAlign,
      }),
      singleValue: (base: any) => ({
        ...base,
        color,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        textDecoration,
        textAlign,
      }),
      input: (base: any) => ({
        ...base,
        color,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        textDecoration,
        textAlign,
      }),
      placeholder: (base: any) => ({
        ...base,
        color,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        textDecoration,
        textAlign,
      }),
      option: (base: any) => ({
        ...base,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        textDecoration,
      }),
    };
  };

  return (
    <Flex
      className={cx("DatasheetsResultDatasheetTableFieldRow", {
        "DatasheetsResultDatasheetTableFieldRow--selected": selected,
      })}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;

        // Don't select row if clicking on buttons
        if (target.closest("button, .Button, .ActionsButton")) {
          return;
        }

        // Don't select row if clicking on dropdown menu (but allow selection when clicking dropdown control)
        if (isDropdownMenuElement(target)) {
          // Don't select row when interacting with dropdown menu
          return;
        }

        // Always select row when clicking on it, even if clicking on cells
        // This ensures copy/delete buttons work
        // Note: Clicking on dropdown control will select the row, which is desired
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.closest("input") || target.closest("textarea")) {
          // Still select the row, but allow the input/textarea to receive focus
          handleClick();
        } else {
          // For other clicks (including dropdown controls), select the row normally
          handleClick();
        }
      }}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        // Also handle mousedown to catch dropdown menu interactions early
        const target = e.target as HTMLElement;

        // Don't select row if clicking on dropdown menu (portal rendered)
        if (isDropdownMenuElement(target)) {
          // Prevent row selection when clicking on dropdown menu
          e.stopPropagation();
          return;
        }
      }}
    >
      {columns.map((column) => {
        const field = column.field;
        const value = getCellStringValue(field.fieldid);
        const fieldType = field.fieldtype.typeid;
        const fieldContentStyle =
          tableContentStyle ?? getFieldContentStyle(field);
        const dropdownContentColor = fieldContentStyle?.color;
        const dropdownStyles = buildDropdownStyles(fieldContentStyle);
        let fieldComponent = (
          <TextField
            value={value}
            onChange={(v) => onCellChange(data.rrn, field, v)}
            editable={editable}
            style={fieldContentStyle}
          />
        );

        if (fieldType === FieldType.number) {
          fieldComponent = (
            <TextField
              value={value}
              onChange={(v) => onCellChange(data.rrn, field, v)}
              validate={(x) => isNumber(x)}
              editable={editable}
              style={fieldContentStyle}
            />
          );
        }

        if (fieldType === FieldType.integer) {
          fieldComponent = (
            <TextField
              value={value}
              onChange={(v) => onCellChange(data.rrn, field, v)}
              validate={(x) =>
                stringHasInteger(x, {
                  allowEmpty: true,
                  allowNegative: false,
                  allowZero: true,
                })
              }
              editable={editable}
              style={fieldContentStyle}
            />
          );
        }

        if (fieldType === FieldType.dropdown) {
          const options = normalizeDropdownOptions(field as any);
          fieldComponent = (
            <div
              style={{ width: "100%" }}
              onClick={(e) => {
                // Ensure row is selected when clicking on dropdown cell
                // Stop propagation to prevent double-selection, but still trigger row click
                e.stopPropagation();
                // Manually trigger row selection
                handleClick();
              }}
              onMouseDown={(e) => {
                // Select row on mousedown as well to ensure it's selected before dropdown opens
                if (!isDropdownMenuElement(e.target)) {
                  handleClick();
                }
              }}
            >
              <Dropdown
                fullWidth
                defaultValue={value}
                className="NewTableFilters__filter-type"
                selectedValue={value ?? DROPDOWN_NO_VALUE}
                searchable={false}
                options={options as any}
                styles={dropdownStyles}
                dropdownContentColor={dropdownContentColor}
                components={{
                  Option: ColoredOption,
                  SingleValue: ColoredSingleValue,
                }}
                onValueChanged={(id: any) => {
                  if (!editable) return;
                  dropdownJustCommittedFieldIdsRef.current.add(field.fieldid);
                  onCellChange(
                    data.rrn,
                    field,
                    id === DROPDOWN_NO_VALUE ? "" : id,
                  );
                  blurActiveElementSoon();
                }}
                onMenuClose={() => {
                  if (!editable) return;
                  setTimeout(() => {
                    dropdownJustCommittedFieldIdsRef.current.delete(field.fieldid);
                  }, 0);
                }}
                disabled={!editable as any}
              />
            </div>
          );
        }

        if (fieldType === FieldType.date) {
          const selectedDate = parseApiDateString(value);

          const commitValue = (typed: string) => {
            if (!editable) return;

            // Always forward raw value to controller.
            // Controller handles validation, normalization, and error popup.
            onCellChange(data.rrn, field, typed ?? "");
          };

          fieldComponent = (
            <div style={{ width: "100%" }}>
              <DatePicker
                selected={selectedDate}
                disabled={!editable}
                readOnly={!editable}
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/D/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                customInput={
                  <input
                    style={{ width: "100%", ...(fieldContentStyle || {}) }}
                  />
                }
                onChangeRaw={(e) => {
                  if (editable && e) {
                    dateRawInputRef.current[field.fieldid] =
                      (e.target as HTMLInputElement)?.value ?? "";
                  }
                }}
                onSelect={(date: Date | null) => {
                  if (!editable || !date) return;
                  // Picker-driven selection — clear raw input
                  delete dateRawInputRef.current[field.fieldid];
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  onCellChange(data.rrn, field, `${year}-${month}-${day}`);
                }}
                onChange={() => {
                  // Required prop — actual handling is in onSelect / onBlur / onKeyDown
                }}
                onKeyDown={(e) => {
                  if (!editable) return;
                  if (e.key === "Enter") {
                    const typed = (e.target as HTMLInputElement)?.value ?? "";
                    delete dateRawInputRef.current[field.fieldid];
                    commitValue(typed);
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onBlur={() => {
                  if (!editable) return;
                  const raw = dateRawInputRef.current[field.fieldid];
                  delete dateRawInputRef.current[field.fieldid];
                  // Only validate if user actually typed (undefined = no typing)
                  if (raw !== undefined) {
                    commitValue(raw);
                  }
                }}
              />
            </div>
          );
        }

        if (fieldType === FieldType.checkbox) {
          const checked = value === "Y" || value === "true";
          fieldComponent = (
            <Checkbox
              defaultChecked={checked}
              onChange={(checkedValue: boolean) => {
                if (!editable) return;
                onCellChange(data.rrn, field, checkedValue);
              }}
            />
          );
        }

        return (
          <div
            style={{ width: column.width, flex: `0 0 ${column.width}px` }}
            key={field.fieldid}
            className="DatasheetsResultDatasheetTableFieldRow__cell"
          >
            {fieldComponent}
          </div>
        );
      })}
    </Flex>
  );
};
